<script lang="ts">
    import type {TableRow} from '../kp-record-detail-table/types';
    import type {TabId} from 'shared/ui-widgets/tabset/types';
    import {AuthorityDetailTabsetService} from './authority-detail-tabset.service';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import KpGenericTabGroup from 'shared/ui-widgets/tabset/KpGenericTabGroup.svelte';
    import KpGenericTabList from 'shared/ui-widgets/tabset/KpGenericTabList.svelte';
    import KpGenericTabItem from 'shared/ui-widgets/tabset/KpGenericTabItem.svelte';
    import KpGenericTabPanels from 'shared/ui-widgets/tabset/KpGenericTabPanels.svelte';
    import KpGenericTabPanelContent from 'shared/ui-widgets/tabset/KpGenericTabPanelContent.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpTabGroupChangeListener from 'shared/ui-widgets/tabset/KpTabGroupChangeListener.svelte';
    import KpPaginatedPanelList from 'shared/components/kp-paginated-panel-list/KpPaginatedPanelList.svelte';
    import KpRecordMarcTable from '../kp-record-marc-table/KpRecordMarcTable.svelte';
    import KpRecordDetailTable from '../kp-record-detail-table/KpRecordDetailTable.svelte';
    import KpRecordOperationMini from '../operation/KpRecordOperationMini.svelte';
    import KpThumbnailMini from '../kp-attachment-mini/KpAttachmentMini.svelte';
    import KpTabGroupConfiguredActiveTabSelector from 'shared/ui-widgets/tabset/KpTabGroupConfiguredActiveTabSelector.svelte';
    import KpRecordInformationTable from '../kp-record-information-table/KpRecordInformationTable.svelte';
    import type {AuthorityTabsConfiguration, DirectoryInsight, Rec, ViewableField} from 'typings/portaro.be.types';

    export let record: Rec;
    export let tabsConfiguration: AuthorityTabsConfiguration;
    export let directoryInsight: DirectoryInsight | undefined;
    export let marcFields: ViewableField[];
    export let detailTableRows: TableRow[] = [];
    export let showInformationTab: boolean;

    const localize = getLocalization();
    const service = getInjector().getByClass(AuthorityDetailTabsetService);
    const operationPageableList = service.createOperationPageableList(record);

    $: attachmentsVisible = directoryInsight && (directoryInsight.sampleFiles.length > 0 || directoryInsight.containsNonCoverFiles || directoryInsight.containsSubdirectories) && tabsConfiguration.attachments.showable;
    $: detailVisible = tabsConfiguration.detail.showable;
    $: marcVisible = tabsConfiguration.marc.showable;
    $: operationsVisible = tabsConfiguration.operations.showable;
    $: informationVisible = showInformationTab;

    const handleTabChange = (event: CustomEvent<TabId>) => {
        if (event.detail === 'tab-operations') {
            operationPageableList.loadFirstPage();
        }
    }
</script>

<KpGenericTabGroup additionalClasses="detail-blok-details">
    <KpTabGroupConfiguredActiveTabSelector config={tabsConfiguration}/>
    <KpTabGroupChangeListener on:tab-change={handleTabChange}/>

    <KpGenericTabList>
        {#if attachmentsVisible}
            <KpGenericTabItem tabId="tab-attachments">
                {localize(/* @kp-localization detail.PrilohyKeStazeni */ 'detail.PrilohyKeStazeni')}
            </KpGenericTabItem>
        {/if}

        {#if detailVisible}
            <KpGenericTabItem tabId="tab-detail">
                {localize(/* @kp-localization detail.detail */ 'detail.detail')}
            </KpGenericTabItem>
        {/if}

        {#if marcVisible}
            <KpGenericTabItem tabId="tab-marc">
                {localize(/* @kp-localization detail.marc */ 'detail.marc')}
            </KpGenericTabItem>
        {/if}

        {#if operationsVisible}
            <KpGenericTabItem tabId="tab-operations">
                {localize(/* @kp-localization record.ProcessingHistory */ 'record.ProcessingHistory')}
            </KpGenericTabItem>
        {/if}

        {#if informationVisible}
            <KpGenericTabItem tabId="tab-information">
                {localize(/* @kp-localization record.RecordInfo */ 'record.RecordInfo')}
            </KpGenericTabItem>
        {/if}

        <KpGenericTabItem tabId="tab-none" hidden={true}></KpGenericTabItem>
    </KpGenericTabList>

    <KpGenericTabPanels>
        {#if attachmentsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-attachments">
                <KpGenericPanel hasBodyPadding={false} hasBorder={false}>
                    <ul class="attachments-list list-group">
                        {#each directoryInsight.sampleFiles as file (file.id)}
                            <li class="list-group-item">
                                <KpThumbnailMini file={file} record={record}/>
                            </li>
                        {/each}
                    </ul>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if detailVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-detail">
                <KpGenericPanel hasBodyPadding={false}>
                    <KpRecordDetailTable rows={detailTableRows}/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if marcVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-marc">
                <KpGenericPanel hasBodyPadding={false}>
                    <KpRecordMarcTable fields={marcFields}/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if operationsVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-operations">
                <KpGenericPanel>
                    <KpPaginatedPanelList pageableList={operationPageableList}
                                          isInPaddedPanel={true}
                                          let:item>
                        <KpRecordOperationMini operation={item}/>
                    </KpPaginatedPanelList>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if informationVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-information">
                <KpGenericPanel hasBodyPadding={false}>
                    <KpRecordInformationTable record={record}/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        <KpGenericTabPanelContent tabPanelId="tab-panel-none" hidden={true}></KpGenericTabPanelContent>
    </KpGenericTabPanels>
</KpGenericTabGroup>

<style lang="less">
    .attachments-list {
        margin: 0;
    }
</style>